import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
} from 'react'

import { cx } from './core/classes.js'
import { mergeRenderProps } from './core/render.js'

/** Standard surface container for cards, panels, and linked boxes. */
export function Box(props: Box.Props) {
  const { children, className, disabled, onClick, padding = 16, render, style, ...rest } = props
  const interactive = Boolean(onClick || render)
  const onClick_own = (() => {
    if (disabled && interactive)
      return (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
      }
    if (onClick) return onClick
    return undefined
  })()
  const ownProps = {
    ...rest,
    'aria-disabled': disabled && interactive ? true : undefined,
    className: cx(
      'overflow-hidden rounded-body border border-border bg-surface text-foreground',
      interactive &&
        'cursor-pointer hover:border-border-hover active:bg-surface-active focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-focus',
      disabled && 'pointer-events-none opacity-50',
      className,
    ),
    'data-disabled': disabled ? '' : undefined,
    ...(onClick_own ? { onClick: onClick_own } : {}),
    style: {
      padding,
      ...style,
    },
  }

  if (render && isValidElement(render))
    return cloneElement(
      render,
      mergeRenderProps(render.props as Record<string, unknown>, ownProps),
      children,
    )

  if (onClick)
    return (
      <button {...ownProps} disabled={disabled} type="button">
        {children}
      </button>
    )

  return <div {...ownProps}>{children}</div>
}

export namespace Box {
  /** Props for {@link Box}. */
  export type Props = HTMLAttributes<HTMLElement> & {
    /** Disable interaction when Box renders as an interactive element. */
    disabled?: boolean | undefined
    /** Padding in px. @default 16 */
    padding?: number | undefined
    /** Element to render as instead of `<div>` or `<button>`. */
    render?: ReactElement | undefined
  }
}
